<template lang="pug" src="./balance-history.pug"> </template>
<style lang="scss" src="./balance-history.scss" scoped></style>
<script>
import { mapState, mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { decimal, numeric, required, maxLength } from 'vuelidate/lib/validators'
import { vLength } from '@/utils/helpers'
import validate from '@/mixins/validate'
import scroll from '@/utils/scroll'
import copyToClipboard from '@/utils/copy'

export default {
    filters: {
        formatOrderType(item, t) {
            return item.order ? t(`$vuetify.pages.balanceHistories.paymentTypes.${item.order.isDelivery ? 'delivery' : 'taxi'}`) : '–'
        }
    },
    mixins: [validationMixin, validate],
    data: () => ({
        filters: {
            fromDate: null,
            toDate: null,
            clientId: null,
            taxiOrderId: null,
            orderType: null,
            paymentType: null,
            paymentId: null,
            phoneNumber: null
        },
        refillBalance: {
            phoneNumber: null,
            amount: null,
            comment: null
        },
        refillDialog: false,
        sorting: {}
    }),
    validations: {
        filters: {
            clientId: {
                numeric
            },
            taxiOrderId: {
                numeric
            },
            paymentId: {
                numeric
            },
            phoneNumber: {
                length: vLength(9)
            }
        },
        refillBalance: {
            phoneNumber: {
                required: function() {
                    return !!this.refillBalance.phoneNumber
                },
                length: function() {
                    return vLength(9)(this.refillBalance.phoneNumber)
                }
            },
            amount: {
                required,
                decimal
            },
            comment: {
                length: maxLength(255)
            }
        }
    },
    computed: {
        ...mapGetters(['permissions']),
        ...mapState({
            balanceHistories: state => state.balanceHistory.balanceHistories,
            pagination: state => state.balanceHistory.pagination,
            appliedFilters: state => state.balanceHistory.filters,
            isTableLoad: state => state.balanceHistory.isLoaded
        }),
        canRefillClientBalance: function() {
            return this.permissions && this.permissions.includes('refill-client-balance')
        },
        headers: function() {
            return [
                { text: 'id', value: 'id' },
                { text: 'clientId', value: '"clientId"' },
                { text: 'phoneNumber', sortable: false },
                { text: 'taxiOrderId', value: '"taxiOrderId"' },
                { text: 'paymentId', value: '"paymentId"' },
                { text: 'change', value: 'change' },
                { text: 'balance', value: 'balance' },
                { text: 'success', value: '"success"' },
                { text: 'cashbackSystemId', value: '"cashbackSystemId"' },
                { text: 'orderType', value: '"orderType"', sortable: false },
                { text: 'paymentType', value: '"paymentType"', sortable: false },
                { text: 'comment', value: 'comment' },
                { text: 'createdAt', value: '"createdAt"' },
                { text: 'updatedAt', value: '"updatedAt"' }
            ].map(it => Object.assign(it, {
                text: this.$vuetify.lang.t(`$vuetify.pages.balanceHistories.table.${it.text}`)
            }))
        },
        paymentTypes() {
            return ['cashback', 'trip', 'delivery', 'refill'].map(type => ({
                text: this.$vuetify.t(`$vuetify.pages.balanceHistories.paymentTypes.${type}`),
                value: type
            }))
        },
        orderTypes() {
            return ['taxi', 'delivery'].map(type => ({
                text: this.$vuetify.t(`$vuetify.pages.balanceHistories.paymentTypes.${type}`),
                value: type
            }))
        }
    },
    watch: {
        refillDialog(isShow) {
            if (!isShow) {
                this.resetRefillBalanceFields()
            }
        },
        sorting: {
            handler() {
                this.getBalanceHistoryByPage()
            },
            deep: true
        }
    },
    created: function() {
        this.filters = Object.assign({}, this.appliedFilters)
        this.getBalanceHistoryByPage(this.pagination.page)
    },
    beforeRouteLeave: function(to, from, next) {
        this.$store.commit('balanceHistory/SET_PAGINATION', { page: 1, length: null })
        next()
    },
    methods: {
        changeToDate(date) {
            this.filters.toDate = date
        },
        changeFromDate(date) {
            this.filters.fromDate = date
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.getBalanceHistoryByPage(page, true)
            }
        },
        reset() {
            this.filters = Object.assign({}, {
                fromDate: new Date(+new Date() - 2419200000).toISOString().split('T')[0],
                toDate: new Date().toISOString().split('T')[0],
                clientId: null,
                taxiOrderId: null,
                paymentId: null,
                paymentType: null,
                orderType: null,
                phoneNumber: null
            })
            this.$store.commit('balanceHistory/RESET_BALANCE_HISTORIES')
            this.getBalanceHistoryByPage()
            this.$v.reset()
        },
        getBalanceHistoryByPage(page = 1, isScrollEnable = false, excel = false) {
            if (this.$v.filters.$invalid) {
                this.$v.$touch()
            } else {
                this.$store.dispatch('balanceHistory/getBalanceHistories', {
                    page,
                    excel,
                    column: this.sorting.sortBy,
                    direction: this.sorting.descending ? 'desc' : 'asc',
                    ...this.appliedFilters,
                    phoneNumber: this.filters.phoneNumber && `380${this.filters.phoneNumber}`
                }).then(_ => {
                    if (isScrollEnable) {
                        scroll(this.$refs.table)
                    }
                })
            }
        },
        openRefillDialog() {
            this.$v.$reset()
            this.refillDialog = true
        },
        closeRefillDialog() {
            this.refillDialog = false
        },
        resetRefillBalanceFields() {
            this.refillBalance = {
                phoneNumber: null,
                amount: null
            }
            this.$v.$reset()
        },
        async refillClientBalance() {
            if (this.$v.refillBalance.$invalid) {
                this.$v.$touch()
            } else {
                const res = await this.$store.dispatch('balanceHistory/refillClientBalance', {
                    phoneNumber: '380' + this.refillBalance.phoneNumber,
                    amount: +this.refillBalance.amount,
                    comment: this.refillBalance.comment
                })
                this.resetRefillBalanceFields()
                if (res && res.data) {
                    this.closeRefillDialog()

                    this.getBalanceHistoryByPage()
                }
            }
        },
        submit() {
            this.$store.commit('balanceHistory/SET_FILTERS', Object.assign({}, this.filters))
            this.getBalanceHistoryByPage()
        },
        copy(text) {
            if (text) {
                copyToClipboard(text)
            }
        },
        exportToExcel() {
            this.getBalanceHistoryByPage(1, false, true)
        },
        checkPaymentType(item) {
            const translationPath = '$vuetify.pages.balanceHistories.paymentTypes'
            const { cashbackSystemId, LTSystemId, RASystemId, taxiOrderId, order } = item

            if (!cashbackSystemId && !RASystemId && !LTSystemId) {
                if (order && order.isDelivery) {
                    return this.$vuetify.t(`${translationPath}.delivery`)
                }

                if (taxiOrderId) {
                    return this.$vuetify.t(`${translationPath}.trip`)
                }
            }

            const translationKey = (cashbackSystemId || LTSystemId || RASystemId) ? 'cashback' : 'refill'
            return this.$vuetify.t(
                `$vuetify.pages.balanceHistories.paymentTypes.${translationKey}`
            )
        }
    }
}
</script>
